import { createSelector } from '@reduxjs/toolkit';
import config from 'Cloud/config';
import localState from 'lib/store';
import { filter, find, groupBy, path, propEq, sortBy, values } from 'ramda';
import { TARIFFS_LIST } from 'reactApp/appHelpers/configHelpers';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import {
    getFeatureAbExclude256GbDesktop,
    getFeatureAbExclude256GbMobile,
    getFeatureAbSpecialDiscounts,
} from 'reactApp/modules/features/features.selectors';
import { PaidInfoSelectors } from 'reactApp/modules/paidInfo/paidInfo.selectors';
import {
    DEFAULT_PRIMARY_PRODUCT,
    filterTariffsByQuotaAndPeriod,
    groupDefinition,
    isNotSpecialProduct,
    isTariffForDiscountAb,
    PROMO_TARIFFS_REGEX,
    SINGLE_QUOTA_DISKO_REGEX,
    SPACE_SIZES,
    YEAR_COST_DECREASE_SINGLE_QUOTA_REGEX,
} from 'reactApp/modules/products/products.helpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { RootState } from 'reactApp/store';
import { Product, Tariff } from 'reactApp/types/Billing';
import { findHigherTariff } from 'reactApp/utils/findTariff';
import { getTariffProductsByPeriods, isMonthPeriod, isYearPeriod } from 'reactApp/utils/Period';

const { product_id: countdownTariffId, id: countdownId } = config.get('PROMO_COUNTDOWN') || {};

const getProductsState = (state: RootState) => state.products;

const getTariffs = createSelector(getProductsState, (productsState) => productsState.list);

const getLifeCycleState = createSelector(getProductsState, (productsState) => ({
    isLoaded: productsState.isLoaded,
    isLoading: productsState.isLoading,
    hasError: productsState.hasError,
}));

export const getProductById = createSelector(
    getTariffs,
    (state, id) => id,
    (products, id) => {
        let product;
        values(products).forEach((item) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const fProduct = find(propEq('id', id), item.products);
            if (fProduct) {
                product = fProduct;
            }
        });

        return product;
    }
);

const getTariffById = createSelector(
    getTariffs,
    (state, id) => id,
    (products, id) => path([id], products)
);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const sortBySpace = createSelector(getTariffs, (products) => sortBy(path(['space', 'original']), values(products)));

const getAvailableProducts = createSelector(sortBySpace, (products) =>
    products.reduce((acc, cur) => {
        const productsList = cur.products.filter(
            (product) => product.available && (product.isPromo || product.hasTrial || TARIFFS_LIST.includes(product.id))
        );

        if (productsList.length) {
            return [
                ...acc,
                {
                    ...cur,
                    products: productsList,
                },
            ];
        }
        return acc;
    }, [])
);

// На декстопе не надо показывать тарифы меньше 128GB. cloudweb-13079.
const getAvailableProductsAbove128GbWeb = createSelector(
    getAvailableProducts,
    EnvironmentSelectors.isPhone,
    // tempexp_13909-start
    getFeatureAbExclude256GbDesktop,
    getFeatureAbExclude256GbMobile,
    // tempexp_13909-end
    (products, isPhone, abDesktop, abMobile) => {
        // tempexp_13909-start
        if (!isPhone && abDesktop === 'b') {
            return products.filter(
                (product) => product?.space?.original >= SPACE_SIZES._128 && product?.space?.original !== SPACE_SIZES._256
            );
        }
        if (!isPhone) {
            return products.filter((product) => product?.space?.original >= SPACE_SIZES._128);
        }
        if (isPhone && abMobile === 'b') {
            return products.filter((product) => product?.space?.original !== SPACE_SIZES._256);
        }
        return products;
        // tempexp_13909-end
    }
);

const getPromoRegex = (state, promo) => PROMO_TARIFFS_REGEX[promo];

const getPromoTariffs = createSelector(getPromoRegex, getAvailableProductsAbove128GbWeb, (promoRegex, products) => {
    if (!promoRegex) {
        return [];
    }

    return products.filter((product) => promoRegex.test(product.id));
});

// tempexp_14156-start
const getDiscountSingleQuotaTariffs = createSelector(getAvailableProductsAbove128GbWeb, (products) =>
    products.filter((product) => YEAR_COST_DECREASE_SINGLE_QUOTA_REGEX.test(product.id))
);
// tempexp_14156-end

const getThreeDecreaseTariffs = createSelector(sortBySpace, (products) => {
    const regex = /.*_decrease2020/;
    return products.filter((product) => regex.test(product.id));
});

const getQuotaDiscountTariffs = createSelector(sortBySpace, (products) => {
    const regex = /.*_banner2020_discount10/;
    return products.filter((product) => regex.test(product.id));
});

const getExpiredDeviceTariff = createSelector(
    sortBySpace,
    (state, quota) => quota,
    (state, quota, period) => period,
    (tariffs, quota, period) => {
        const regex = /.*_ended_subscription/;
        return tariffs
            .filter((tariff) => regex.test(tariff.id))
            .filter((tariff) => filterTariffsByQuotaAndPeriod(tariff, quota, period))?.[0];
    }
);

const getQuotaDiscountTariffForUser = createSelector(getQuotaDiscountTariffs, UserSelectors.getCloudSpace, (tariffs, userSpace) => {
    const { total } = userSpace;

    return findHigherTariff({ tariffs, quota: total.original, period: '' }) || tariffs.pop();
});

const getProTariffForUser = createSelector(getAvailableProducts, UserSelectors.getCloudSpace, (tariffs, userSpace) => {
    const { total } = userSpace;

    const proTariffs = tariffs.filter((tariff) => tariff.products.some((product) => product.isProfessional));

    return findHigherTariff({ tariffs: proTariffs, quota: total.original, period: '' }) || proTariffs.pop();
});

const getPromoCodeTrialTariffs = createSelector(getAvailableProducts, (products) => {
    return products.filter((tariff) => tariff.isPromoCode && tariff.products.some((product) => product.hasTrial));
});

const getTrialTariffs = createSelector(getAvailableProducts, (products) => {
    return products.filter((tariff) => tariff.products.some((product) => product.hasTrial));
});

const getTariffsByQuotaHelper = (tariffs, quota) => filter((tariff) => path(['space', 'original'], tariff) === +quota, tariffs);

const getNonSpecialTariffs = createSelector(getAvailableProductsAbove128GbWeb, getTariffById, (products, additionalTariff = false) => {
    let tariffs = [];

    if (additionalTariff) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const isCountdownPromoTariff = additionalTariff.id === countdownTariffId;
        const email = config.get('user.email');
        const countdownEndDate = localState.get(`${email}|promo-countdown_${countdownId}`);

        tariffs.push({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            ...additionalTariff,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            isPromoTariff: true,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            isCountdownPromoTariff,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            countdownEndDate,
        });
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    tariffs = [...tariffs, ...products.filter((tariff) => isNotSpecialProduct(tariff) && !tariff.isSingleQuota)];
    const defaultProducts = products.filter(
        (t) => isNotSpecialProduct(t) && !t.products.some((p) => p.isProfessional) && t.products.some((p) => isMonthPeriod(p.period))
    );

    // FIXME Пофиксить ПОСЛЕ https://jira.vk.team/browse/CLOUDWEB-13535
    tariffs.forEach((tariff) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const yearCustomTariff = tariff.products.find((p) => p.id.match(YEAR_COST_DECREASE_SINGLE_QUOTA_REGEX));
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (!tariff.products.some((p) => p.isProfessional) && yearCustomTariff) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const defaultTariff = getTariffsByQuotaHelper(defaultProducts, tariff.space.original)[0];
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (!defaultTariff.products.some(({ id }) => id === yearCustomTariff.id)) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                defaultTariff.products.push(yearCustomTariff);

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                tariffs = tariffs.filter(({ id }) => id !== tariff.id);
            }
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (!tariff.products.some((p) => p.isProfessional) && !yearCustomTariff) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const defaultTariff = getTariffsByQuotaHelper(defaultProducts, tariff.space.original)[0];
        if (defaultTariff) {
            const monthlyDefaultPrice = path(
                ['price'],
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                defaultTariff.products.find((p) => isMonthPeriod(p.period))
            );
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            tariff.products = tariff.products.map((p) => ({ ...p, monthlyDefaultPrice }));
        }
    });

    return tariffs;
});

const getSingleQuotaTariffs = createSelector(getAvailableProductsAbove128GbWeb, (tariffs) => {
    return tariffs.filter((tariff) => isNotSpecialProduct(tariff) && tariff.isSingleQuota);
});

// TODO После того как логика зафиксируется, возможно стоит написать юнит
const getNonSpecialSingleQuoteTariffs = createSelector(
    [(state: RootState) => getNonSpecialTariffs(state, false), getAvailableProductsAbove128GbWeb, getFeatureAbSpecialDiscounts],
    (nonSpecialTariffs: Tariff[], tariffs: Tariff[], abSpecialDiscounts) => {
        const proTariffs = tariffs.filter((tariff) => tariff.isSingleQuota && tariff.id.match(SINGLE_QUOTA_DISKO_REGEX));
        const usualTariffs = tariffs.filter(
            (tariff) =>
                tariff.isSingleQuota &&
                !tariff.id.match(SINGLE_QUOTA_DISKO_REGEX) &&
                isNotSpecialProduct(tariff) &&
                // tempexp_14156-next-line
                isTariffForDiscountAb(tariff, abSpecialDiscounts)
        );

        const result: Tariff[] = [];

        for (const tariff of [...proTariffs, ...usualTariffs]) {
            if (tariff.products.length > 1 || !isYearPeriod(tariff.products[0].period)) {
                continue;
            }
            const isPro = tariff.id.match(SINGLE_QUOTA_DISKO_REGEX);
            const tariffsByQuota = getTariffsByQuotaHelper(isPro ? proTariffs : usualTariffs, tariff.space.original) as Tariff[];

            const yearNotMergedTariff = tariff.products[0];
            const monthNotMergedTariff = tariffsByQuota.find((tariff) => !isYearPeriod(tariff.products[0].period))?.products?.[0];

            if (monthNotMergedTariff) {
                result.push({
                    ...tariff,
                    products: [yearNotMergedTariff, monthNotMergedTariff],
                });
            }
        }

        return result;
    }
);

const getTariffsActivatedByPromocode = createSelector(getAvailableProducts, (tariffs) => {
    if (!tariffs.length) {
        return [];
    }

    return tariffs.filter((tariff) => tariff.products.some((product) => product.isPromo && !product.hasTrial));
});

const getTariffsByQuota = createSelector(
    getNonSpecialTariffs,
    getNonSpecialSingleQuoteTariffs,
    (state, quota) => quota,
    (nonSpecialTariffs, singleQuotaNonSpecialTariffs, quota) =>
        getTariffsByQuotaHelper([...singleQuotaNonSpecialTariffs, ...nonSpecialTariffs], quota)
);

/**
 * В тарифах в продукты могут быть замерджены месячные или годовые, а может и нет
 * НО мы точно знаем, что в единой квоте селектор возвращает замердженные, по этому на всякий случай проверяем
 * есть ли выбранный тариф в единой квоте. Так как замердженный в приоритете, потому что у него есть и годовой и месячный продукт
 * */
const getTariffByProductId = createSelector(
    sortBySpace,
    getNonSpecialSingleQuoteTariffs,
    (state, id) => id,
    (tariffs, singleQuotaTariffs, id) => {
        const findByProductPredicate = (tariff: Tariff) => tariff.products.find((product) => product.id === id);

        return singleQuotaTariffs.find(findByProductPredicate) || tariffs.find(findByProductPredicate);
    }
);

const getPrimaryTariff = (state, primaryProduct = DEFAULT_PRIMARY_PRODUCT) => {
    if (!isNaN(primaryProduct * 1)) {
        const tariffs = getTariffsByQuota(state, primaryProduct);
        if (!tariffs?.length) {
            return;
        }
        // @ts-ignore
        if (tariffs[0].products?.length === 1 && tariffs.length > 1) {
            return {
                // @ts-ignore
                ...tariffs[0],
                // @ts-ignore
                products: [...tariffs[0].products, ...tariffs[1].products],
            };
        }
        return tariffs[0];
    }

    // Пришли из письма и явно передали ID
    const product = getTariffByProductId(state, primaryProduct);

    return product ? product : getTariffsByQuota(state, DEFAULT_PRIMARY_PRODUCT)[0];
};

const getPrimaryProduct = createSelector(
    getTariffsActivatedByPromocode,
    getNonSpecialTariffs,
    getPrimaryTariff,
    (state, productId: string) => productId,
    (tariffsActivatedByPromoCodes, tariffs, primaryTariff, productId) => {
        if (!primaryTariff) {
            return;
        }

        let tariff = primaryTariff;

        if (tariffsActivatedByPromoCodes.length && !productId) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const sortedBySpace = sortBy(path(['space', 'original']), tariffsActivatedByPromoCodes);
            tariff = sortedBySpace[sortedBySpace.length - 1];
        }

        const { yearProduct, monthProduct } = getTariffProductsByPeriods(tariff?.products);

        return yearProduct || monthProduct;
    }
);

const getMonthProductByQuota = createSelector(
    getSingleQuotaTariffs,
    (state, quota) => quota,
    (tariffs, quota) => {
        const monthTariffs = tariffs.filter((tariff) => tariff.products.some((product) => isMonthPeriod(product.period)));

        const tariff = findHigherTariff({ tariffs: monthTariffs, quota, period: null }) || monthTariffs.pop();

        return tariff && tariff.products.find((product) => isMonthPeriod(product.period));
    }
);

const getMailTrialByQuota = createSelector(
    getAvailableProducts,
    (state, quota) => quota,
    (tariffs, quota) => {
        const monthTrials = tariffs.filter((tariff) => /.*1m_single_quota_trial_cloud$/.test(tariff.id));

        const tariff = findHigherTariff({ tariffs: monthTrials, quota, period: null }) || monthTrials.pop();

        return tariff && tariff.products.find((product) => isMonthPeriod(product.period));
    }
);

const getSelectedGroup = createSelector(getProductsState, (productsState) => productsState.selectedGroupName);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const getDefaultGroup = (state, groupType) => groupType && groupDefinition.get(groupType)?.filter((a) => a.isDefault)[0].id;

const getSpecialTariff = createSelector(
    (state, config) => getTariffByProductId(state, path(['data', 'productId'], config)),
    (state, config) => path(['data', 'discount'], config),
    (tariff, discount) => {
        if (tariff) {
            return {
                ...tariff,
                isSpecial: true,
                discount,
            };
        }
    }
);

const isTrialAvailable = createSelector(
    (state, id) => getProductById(state, id),
    (state) => PaidInfoSelectors.isUserWithoutPayment(state),
    (product, isUserWithoutPayment) => product && isUserWithoutPayment
);

const getProductsGroupBySpace = createSelector(getNonSpecialTariffs, (tariffs) => {
    const products = tariffs
        .reduce((acc: Product[], cur: Tariff) => {
            const { yearProduct, monthProduct } = getTariffProductsByPeriods(cur.products);

            if (!yearProduct?.isProfessional) {
                acc.push(yearProduct);
            }

            if (!monthProduct?.isProfessional) {
                acc.push(monthProduct);
            }

            return acc;
        }, [])
        .filter(Boolean);

    return groupBy((product) => String(product.space?.original), products);
});

const getPromoTariffsGroupedBySpace = createSelector(
    getPromoRegex,
    getPromoTariffs,
    getNonSpecialTariffs,
    (promoreg, promoTariffs: Tariff[], nonSpecialTariffs: Tariff[]) => {
        const yearPromoProducts = promoTariffs.map(({ products }) => products[0]);
        const monthRegularProducts = nonSpecialTariffs.reduce<Product[]>((acc, cur) => {
            const monthProduct = cur.products.find((product) => isMonthPeriod(product.period));
            if (monthProduct) {
                acc.push(monthProduct);
            }
            return acc;
        }, []);

        const products = [...yearPromoProducts, ...monthRegularProducts];
        return groupBy((product) => String(product.space?.original), products);
    }
);

export const ProductsSelectors = {
    getTariffs,
    getLifeCycleState,
    getProductById,
    getTariffById,
    sortBySpace,
    getAvailableProducts,
    getPromoTariffs,
    getThreeDecreaseTariffs,
    getQuotaDiscountTariffForUser,
    getProTariffForUser,
    getPromoCodeTrialTariffs,
    getTrialTariffs,
    getNonSpecialSingleQuoteTariffs,
    getNonSpecialTariffs,
    getTariffsActivatedByPromocode,
    getTariffByProductId,
    getMonthProductByQuota,
    getMailTrialByQuota,
    getTariffGroups: (groupType) => groupDefinition.get(groupType),
    getSelectedGroup,
    getDefaultGroup,
    getSpecialTariff,
    isTrialAvailable,
    getProductsGroupBySpace,
    getPromoTariffsGroupedBySpace,
    getPrimaryProduct,
    getExpiredDeviceTariff,
    getDiscountSingleQuotaTariffs,
};
